import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type {
  IOffboarding,
  IOffboardingListResponse,
  IOffboardingInvitation,
  IOffboardingApprover,
  IPendingApprovalOffboarding,
  IOffboardingDocument,
  IOffboardingSettlement,
  IOffboardingAssetClearance,
  ICreateOffboardingPayload,
  ISendInvitationPayload,
  IUpdateSettlementPayload,
  ISettlementPreview,
} from "@/types/(company)/offboarding";
import {
  API_COMPANY_OFFBOARDINGS,
  API_COMPANY_OFFBOARDING_BY_ID,
  API_COMPANY_OFFBOARDING_CANCEL,
  API_COMPANY_OFFBOARDING_INVITATION,
  API_COMPANY_OFFBOARDING_SEND_INVITATION,
  API_COMPANY_OFFBOARDING_ACCEPT_INVITATION,
  API_COMPANY_OFFBOARDING_SKIP_INVITATION,
  API_COMPANY_OFFBOARDING_ASSET_CLEARANCE,
  API_COMPANY_OFFBOARDING_COMPLETE_ASSET_CLEARANCE,
  API_COMPANY_OFFBOARDING_APPROVERS,
  API_COMPANY_OFFBOARDING_REMOVE_APPROVER,
  API_COMPANY_OFFBOARDING_APPROVE,
  API_COMPANY_OFFBOARDING_REJECT,
  API_COMPANY_OFFBOARDING_PENDING_APPROVALS,
  API_COMPANY_OFFBOARDING_BLOCKED_EMPLOYEE_IDS,
  API_COMPANY_OFFBOARDING_DOCUMENTS,
  API_COMPANY_OFFBOARDING_DOCUMENT_BY_ID,
  API_COMPANY_OFFBOARDING_SEND_FOR_SIGNATURE,
  API_COMPANY_OFFBOARDING_COMPLETE_DOCUMENTS,
  API_COMPANY_OFFBOARDING_SETTLEMENT,
  API_COMPANY_OFFBOARDING_SUBMIT_TO_PAYROLL,
  API_COMPANY_OFFBOARDING_SETTLEMENT_PREVIEW,
  API_COMPANY_OFFBOARDING_SKIP_SETTLEMENT,
  API_COMPANY_OFFBOARDING_REBOARD,
} from "@/constants/apiEndpoints";

export const companyOffboardingApi = createApi({
  reducerPath: "companyOffboardingApi",
  baseQuery: dynamicBaseQuery,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "offboardingList",
    "offboardingDetail",
    "offboardingInvitation",
    "offboardingApprovers",
    "offboardingPendingApprovals",
    "offboardingDocuments",
    "offboardingSettlement",
    "offboardingAssetClearance",
  ],
  endpoints: (builder) => ({
    // ── LIST ──────────────────────────────────────────────────────────────────
    companyGetOffboardings: builder.query<
      IOffboardingListResponse,
      Record<string, any>
    >({
      query: (params) => ({ url: API_COMPANY_OFFBOARDINGS, params }),
      providesTags: ["offboardingList"],
    }),

    // ── DETAIL ────────────────────────────────────────────────────────────────
    companyGetOffboardingById: builder.query<
      IApiResponse<IOffboarding>,
      number
    >({
      query: (id) => ({ url: API_COMPANY_OFFBOARDING_BY_ID(id) }),
      providesTags: (_r, _e, id) => [{ type: "offboardingDetail", id }],
    }),

    // ── CREATE ────────────────────────────────────────────────────────────────
    companyCreateOffboarding: builder.mutation<
      IApiResponse<IOffboarding>,
      ICreateOffboardingPayload
    >({
      query: (body) => ({
        url: API_COMPANY_OFFBOARDINGS,
        method: "POST",
        body,
      }),
      invalidatesTags: ["offboardingList"],
    }),

    // ── UPDATE ────────────────────────────────────────────────────────────────
    companyUpdateOffboarding: builder.mutation<
      IApiResponse<IOffboarding>,
      { id: number; body: Partial<ICreateOffboardingPayload> }
    >({
      query: ({ id, body }) => ({
        url: API_COMPANY_OFFBOARDING_BY_ID(id),
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingDetail", id },
        "offboardingList",
      ],
    }),

    // ── CANCEL ────────────────────────────────────────────────────────────────
    companyCancelOffboarding: builder.mutation<
      IApiResponse<IOffboarding>,
      { id: number; cancellationReason?: string }
    >({
      query: ({ id, ...body }) => ({
        url: API_COMPANY_OFFBOARDING_CANCEL(id),
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingDetail", id },
        "offboardingList",
      ],
    }),

    // ── REBOARD ───────────────────────────────────────────────────────────────
    companyReboardEmployee: builder.mutation<
      IApiResponse<IOffboarding>,
      { employeeId: number; reason?: string; rejoiningDate?: string }
    >({
      query: ({ employeeId, ...body }) => ({
        url: API_COMPANY_OFFBOARDING_REBOARD(employeeId),
        method: "POST",
        body,
      }),
      invalidatesTags: ["offboardingList", "offboardingDetail"],
    }),

    // ── INVITATION ────────────────────────────────────────────────────────────
    companyGetOffboardingInvitation: builder.query<
      IApiResponse<IOffboardingInvitation>,
      number
    >({
      query: (id) => ({ url: API_COMPANY_OFFBOARDING_INVITATION(id) }),
      providesTags: (_r, _e, id) => [{ type: "offboardingInvitation", id }],
    }),

    companySendOffboardingInvitation: builder.mutation<
      IApiResponse<IOffboardingInvitation>,
      { id: number; body: ISendInvitationPayload }
    >({
      query: ({ id, body }) => ({
        url: API_COMPANY_OFFBOARDING_SEND_INVITATION(id),
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingInvitation", id },
        { type: "offboardingDetail", id },
        "offboardingList",
      ],
    }),

    companyAcceptOffboardingInvitation: builder.mutation<
      IApiResponse<IOffboardingInvitation>,
      number
    >({
      query: (id) => ({
        url: API_COMPANY_OFFBOARDING_ACCEPT_INVITATION(id),
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "offboardingInvitation", id },
        { type: "offboardingDetail", id },
        "offboardingList",
      ],
    }),

    companySkipOffboardingInvitation: builder.mutation<
      IApiResponse<IOffboardingInvitation>,
      number
    >({
      query: (id) => ({
        url: API_COMPANY_OFFBOARDING_SKIP_INVITATION(id),
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "offboardingInvitation", id },
        { type: "offboardingDetail", id },
        "offboardingList",
      ],
    }),

    // ── ASSET CLEARANCE ───────────────────────────────────────────────────────
    companyGetOffboardingAssetClearance: builder.query<
      IApiResponse<IOffboardingAssetClearance>,
      number
    >({
      query: (id) => ({ url: API_COMPANY_OFFBOARDING_ASSET_CLEARANCE(id) }),
      providesTags: (_r, _e, id) => [{ type: "offboardingAssetClearance", id }],
    }),

    companyCompleteOffboardingAssetClearance: builder.mutation<
      IApiResponse<IOffboarding>,
      number
    >({
      query: (id) => ({
        url: API_COMPANY_OFFBOARDING_COMPLETE_ASSET_CLEARANCE(id),
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "offboardingDetail", id },
        "offboardingList",
      ],
    }),

    // ── APPROVERS ─────────────────────────────────────────────────────────────
    companyGetOffboardingApprovers: builder.query<
      IApiResponse<IOffboardingApprover[]>,
      number
    >({
      query: (id) => ({ url: API_COMPANY_OFFBOARDING_APPROVERS(id) }),
      providesTags: (_r, _e, id) => [{ type: "offboardingApprovers", id }],
    }),

    companySaveOffboardingApprovers: builder.mutation<
      IApiResponse<IOffboardingApprover[]>,
      { id: number; employeeIds: number[] }
    >({
      query: ({ id, employeeIds }) => ({
        url: API_COMPANY_OFFBOARDING_APPROVERS(id),
        method: "PUT",
        body: { employeeIds },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingApprovers", id },
        { type: "offboardingDetail", id },
      ],
    }),

    companyRemoveOffboardingApprover: builder.mutation<
      IApiResponse<IOffboardingApprover[]>,
      { id: number; approverId: number }
    >({
      query: ({ id, approverId }) => ({
        url: API_COMPANY_OFFBOARDING_REMOVE_APPROVER(id, approverId),
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingApprovers", id },
        { type: "offboardingDetail", id },
      ],
    }),

    companyApproveOffboarding: builder.mutation<
      IApiResponse<IOffboardingApprover>,
      { id: number; message?: string }
    >({
      query: ({ id, message }) => ({
        url: API_COMPANY_OFFBOARDING_APPROVE(id),
        method: "POST",
        body: { message },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingApprovers", id },
        { type: "offboardingDetail", id },
        "offboardingPendingApprovals",
      ],
    }),

    companyRejectOffboarding: builder.mutation<
      IApiResponse<IOffboardingApprover>,
      { id: number; message: string }
    >({
      query: ({ id, message }) => ({
        url: API_COMPANY_OFFBOARDING_REJECT(id),
        method: "POST",
        body: { message },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingApprovers", id },
        { type: "offboardingDetail", id },
        "offboardingPendingApprovals",
      ],
    }),

    companyGetPendingOffboardingApprovals: builder.query<
      IApiResponse<IPendingApprovalOffboarding[]>,
      void
    >({
      query: () => ({ url: API_COMPANY_OFFBOARDING_PENDING_APPROVALS }),
      providesTags: ["offboardingPendingApprovals"],
    }),

    companyGetOffboardingBlockedEmployeeIds: builder.query<
      IApiResponse<number[]>,
      void
    >({
      query: () => ({ url: API_COMPANY_OFFBOARDING_BLOCKED_EMPLOYEE_IDS }),
      providesTags: ["offboardingList"],
    }),

    // ── DOCUMENTS ─────────────────────────────────────────────────────────────
    companyGetOffboardingDocuments: builder.query<
      IApiResponse<IOffboardingDocument[]>,
      number
    >({
      query: (id) => ({ url: API_COMPANY_OFFBOARDING_DOCUMENTS(id) }),
      providesTags: (_r, _e, id) => [{ type: "offboardingDocuments", id }],
    }),

    companyUploadOffboardingDocument: builder.mutation<
      IApiResponse<IOffboardingDocument>,
      { id: number; file: File }
    >({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: API_COMPANY_OFFBOARDING_DOCUMENTS(id),
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingDocuments", id },
        { type: "offboardingDetail", id },
      ],
    }),

    companyUpdateOffboardingDocument: builder.mutation<
      IApiResponse<IOffboardingDocument>,
      {
        id: number;
        docId: number;
        body: { status?: string; visibility?: string };
      }
    >({
      query: ({ id, docId, body }) => ({
        url: API_COMPANY_OFFBOARDING_DOCUMENT_BY_ID(id, docId),
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingDocuments", id },
        { type: "offboardingDetail", id },
      ],
    }),

    companyDeleteOffboardingDocument: builder.mutation<
      void,
      { id: number; docId: number }
    >({
      query: ({ id, docId }) => ({
        url: API_COMPANY_OFFBOARDING_DOCUMENT_BY_ID(id, docId),
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingDocuments", id },
        { type: "offboardingDetail", id },
      ],
    }),

    companySendOffboardingDocumentsForSignature: builder.mutation<
      IApiResponse<{ message: string }>,
      number
    >({
      query: (id) => ({
        url: API_COMPANY_OFFBOARDING_SEND_FOR_SIGNATURE(id),
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "offboardingDocuments", id },
        { type: "offboardingDetail", id },
      ],
    }),

    companyCompleteOffboardingDocuments: builder.mutation<
      IApiResponse<IOffboarding>,
      number
    >({
      query: (id) => ({
        url: API_COMPANY_OFFBOARDING_COMPLETE_DOCUMENTS(id),
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "offboardingDetail", id },
        "offboardingList",
      ],
    }),

    // ── SETTLEMENT ────────────────────────────────────────────────────────────
    companyGetOffboardingSettlement: builder.query<
      IApiResponse<IOffboardingSettlement>,
      number
    >({
      query: (id) => ({ url: API_COMPANY_OFFBOARDING_SETTLEMENT(id) }),
      providesTags: (_r, _e, id) => [{ type: "offboardingSettlement", id }],
    }),

    companyUpdateOffboardingSettlement: builder.mutation<
      IApiResponse<IOffboardingSettlement>,
      { id: number; body: IUpdateSettlementPayload }
    >({
      query: ({ id, body }) => ({
        url: API_COMPANY_OFFBOARDING_SETTLEMENT(id),
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingSettlement", id },
      ],
    }),

    /**
     * `expectedNetPay` is the preview's net the user confirmed; the server
     * refuses (409) a booking that comes to anything else.
     */
    companySubmitOffboardingToPayroll: builder.mutation<
      IApiResponse<IOffboardingSettlement>,
      { id: number; expectedNetPay?: number }
    >({
      query: ({ id, expectedNetPay }) => ({
        url: API_COMPANY_OFFBOARDING_SUBMIT_TO_PAYROLL(id),
        method: "POST",
        body: { expectedNetPay },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingSettlement", id },
        { type: "offboardingDetail", id },
        "offboardingList",
      ],
    }),

    companySkipOffboardingSettlement: builder.mutation<
      IApiResponse<IOffboarding>,
      { id: number; reason: string }
    >({
      query: ({ id, reason }) => ({
        url: API_COMPANY_OFFBOARDING_SKIP_SETTLEMENT(id),
        method: "PATCH",
        body: { reason },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "offboardingSettlement", id },
        { type: "offboardingDetail", id },
        "offboardingList",
      ],
    }),

    // ── SETTLEMENT PREVIEW ───────────────────────────────────────────────────
    /**
     * The settlement as payroll will book it: typed lines (the unsaved draft
     * when sent) plus gratuity, leave encashment and queued reimbursements.
     * Offboarding view is enough. POST only to carry the draft — it writes
     * nothing, so it is a query.
     */
    companyPreviewOffboardingSettlement: builder.query<
      IApiResponse<ISettlementPreview>,
      {
        id: number;
        earnings?: Array<{ name: string; amount: number; isActive?: boolean }>;
        deductions?: Array<{
          name: string;
          amount: number;
          isActive?: boolean;
        }>;
        leaveAdvanceWaiverReason?: string | null;
      }
    >({
      query: ({ id, earnings, deductions, leaveAdvanceWaiverReason }) => ({
        url: API_COMPANY_OFFBOARDING_SETTLEMENT_PREVIEW(id),
        method: "POST",
        body: { earnings, deductions, leaveAdvanceWaiverReason },
      }),
      providesTags: (_r, _e, { id }) => [{ type: "offboardingSettlement", id }],
    }),
  }),
});

export const {
  useCompanyGetOffboardingsQuery,
  useCompanyGetOffboardingByIdQuery,
  useCompanyCreateOffboardingMutation,
  useCompanyUpdateOffboardingMutation,
  useCompanyCancelOffboardingMutation,
  useCompanyReboardEmployeeMutation,
  useCompanyGetOffboardingInvitationQuery,
  useCompanySendOffboardingInvitationMutation,
  useCompanyAcceptOffboardingInvitationMutation,
  useCompanySkipOffboardingInvitationMutation,
  useCompanyGetOffboardingAssetClearanceQuery,
  useCompanyCompleteOffboardingAssetClearanceMutation,
  useCompanyGetOffboardingApproversQuery,
  useCompanySaveOffboardingApproversMutation,
  useCompanyRemoveOffboardingApproverMutation,
  useCompanyApproveOffboardingMutation,
  useCompanyRejectOffboardingMutation,
  useCompanyGetPendingOffboardingApprovalsQuery,
  useCompanyGetOffboardingBlockedEmployeeIdsQuery,
  useCompanyGetOffboardingDocumentsQuery,
  useCompanyUploadOffboardingDocumentMutation,
  useCompanyUpdateOffboardingDocumentMutation,
  useCompanyDeleteOffboardingDocumentMutation,
  useCompanySendOffboardingDocumentsForSignatureMutation,
  useCompanyCompleteOffboardingDocumentsMutation,
  useCompanySkipOffboardingSettlementMutation,
  useCompanyGetOffboardingSettlementQuery,
  useCompanyUpdateOffboardingSettlementMutation,
  useCompanySubmitOffboardingToPayrollMutation,
  useCompanyPreviewOffboardingSettlementQuery,
} = companyOffboardingApi;
